import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Card, Table, Button, Space, message, Popconfirm, Divider, Tag, Tooltip, Modal, Input, Typography, Select, Spin } from "antd";
import { PlusOutlined, ReloadOutlined, DeleteOutlined, EyeOutlined, SyncOutlined, AuditOutlined, LinkOutlined, ScissorOutlined, FolderOutlined, CheckCircleOutlined, EditOutlined, SafetyCertificateOutlined, ClockCircleOutlined, CloseCircleOutlined, WarningOutlined, SearchOutlined, FileTextOutlined, NumberOutlined, SendOutlined, TeamOutlined } from "@ant-design/icons";
import { useTableSelection } from "../../../hooks/useTableSelection";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import { useAuth } from "../../../hooks/useAuth";

import InvoiceDataSourceFilter from "./components/InvoiceDataSourceFilter";
import CollapsibleFilterBar, { formatArraySummary } from "../../../components/common/CollapsibleFilterBar";
import UploadDrawer from "../APReconciliationWorkbench/components/upload/UploadDrawer";
import InvoiceDataSourceDetailDrawer from "./components/InvoiceDataSourceDetailDrawer";
import InvoiceDataSourceActionMenu from "./components/InvoiceDataSourceActionMenu";
import BatchUpdateBatchNumberModal from "./components/BatchUpdateBatchNumberModal";
import BatchAssignPartnerModal from "./components/BatchAssignPartnerModal";
import BatchActionsBar from "../../../components/common/BatchActionsBar";

import { useInvoiceDataSourceActions } from "./hooks/useInvoiceDataSourceActions";
import { DEFAULT_FILTER_VALUES } from "./hooks/useInvoiceDataSourceFilters";
import { settlementService, contractManagementService } from "../../../api/enhancedApi";

import { INVOICE_DATA_SOURCE_STATUS_LABELS, INVOICE_DATA_SOURCE_STATUS_COLORS, SOURCE_TYPE_LABELS, SOURCE_TYPE_COLORS, STANDARD_COST_TYPE_LABELS, STANDARD_COST_TYPE_COLORS, idsIsProcessing, idsIsTerminal } from "../constants";
import { hasPerIdsPipelineActions } from "../APReconciliationWorkbench/pipeline";
import { getIdsDeleteMode } from "../APReconciliationWorkbench/utils/deleteSemantics";
import { getReplacementId, getReplacementLabel, getReplacesId, getReplacesLabel, getVoidReason } from "../utils/revisionDisplay";

// 默认的 status 和 source_type 值（用于判断是否为非默认筛选）
const DEFAULT_STATUS_SET = new Set(DEFAULT_FILTER_VALUES.api.status.split(","));
const DEFAULT_SOURCE_TYPE_SET = new Set(DEFAULT_FILTER_VALUES.api.source_type.split(","));

const idsBatchSelectionDisabled = (record) => idsIsProcessing(record?.status) || idsIsTerminal(record?.status);

const getDeleteConfirmDescription = (record) => {
    const deleteMode = getIdsDeleteMode(record);
    if (deleteMode === "running") {
        return "该记录仍在处理，删除时后端会先停止关联执行，停止成功后才会删除。";
    }
    if (deleteMode === "protected") {
        return "该记录已确认入库或已作废，后端会阻断删除。";
    }
    return "确定要删除这条记录吗？";
};

const InvoiceDataSourcePage = () => {
    const { TextArea } = Input;
    const { Text } = Typography;

    // 获取权限相关功能
    const { hasPermission, loading: authLoading, user } = useAuth();

    // 状态管理
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条记录`,
    });

    // 使用表格选择Hook
    const {
        selectedRowKeys,
        selectedRows,
        tableRef: selectionTableRef,
        rowSelection,
        getRowProps,
        clearSelection,
        getIndexColumn,
        handleRowSelectionChange,
    } = useTableSelection({
        rowKey: "id",
        dataSource: data || [],
        pagination,
        isRowSelectionDisabled: idsBatchSelectionDisabled,
    });

    const [uploadDrawerVisible, setUploadDrawerVisible] = useState(false);
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [batchUpdateModalVisible, setBatchUpdateModalVisible] = useState(false);
    const [assignPartnerModalVisible, setAssignPartnerModalVisible] = useState(false);

    // 过滤条件 - 存储已格式化的API参数（使用共享常量 + 默认创建人筛选）
    // admin（超级管理员）默认看到所有人的数据，普通用户默认只看自己的
    const [filters, setFilters] = useState(() => ({
        ...DEFAULT_FILTER_VALUES.api,
        ...(user?.id && !user?.is_superuser ? { created_by_user_id: user.id } : {}),
    }));

    // 当用户信息延迟加载完成时，补充默认的创建人筛选（仅非 admin）
    const creatorInitializedRef = useRef(!!user?.id);
    useEffect(() => {
        if (!creatorInitializedRef.current && user?.id) {
            creatorInitializedRef.current = true;
            if (!user.is_superuser) {
                setFilters((prev) => ({ ...prev, created_by_user_id: user.id }));
            }
        }
    }, [user]);

    // 供应商 ID→名称 映射（用于摘要栏展示供应商名称）
    const [supplierMap, setSupplierMap] = useState({});
    useEffect(() => {
        const loadSuppliers = async () => {
            try {
                const response = await contractManagementService.businessPartner.getSuppliers({
                    is_active: true, page: 1, page_size: 500,
                });
                if (response.data && Array.isArray(response.data.items)) {
                    const map = {};
                    response.data.items.forEach((s) => {
                        // 摘要中用简短名称：优先 code，否则截取 name 前 8 字符
                        map[s.id] = s.code || (s.name.length > 8 ? s.name.substring(0, 8) + ".." : s.name);
                    });
                    setSupplierMap(map);
                }
            } catch (error) {
                console.error("加载供应商映射失败:", error);
            }
        };
        loadSuppliers();
    }, []);

    // 快捷搜索（常驻在摘要栏的搜索框）
    const [quickSearchValue, setQuickSearchValue] = useState("");
    const quickSearchDebounceRef = useRef(null);

    // 账单内容搜索（常驻在摘要栏，搜索 OCR 识别结果）
    const [invoiceContentSearchValue, setInvoiceContentSearchValue] = useState("");
    const invoiceContentDebounceRef = useRef(null);

    // 计算筛选摘要项 —— 将 API 格式 filters 翻译为用户可读标签
    const filterSummaryItems = useMemo(() => {
        const items = [];

        // 状态筛选 —— 始终显示（用户需要知道当前看的是哪些状态）
        if (filters.status) {
            const statusArr = filters.status.split(",");
            const allLabels = statusArr.map((s) => INVOICE_DATA_SOURCE_STATUS_LABELS[s] || s);
            const fullValue = allLabels.join(" / ");
            const isDefault = statusArr.length === DEFAULT_STATUS_SET.size && statusArr.every((s) => DEFAULT_STATUS_SET.has(s));
            const value = formatArraySummary(statusArr, INVOICE_DATA_SOURCE_STATUS_LABELS);
            items.push({
                label: "状态",
                value: isDefault ? `${statusArr.length}项 (默认)` : value,
                fullValue,
            });
        }

        // 批次号
        if (filters.batch_number) {
            items.push({ label: "批次号", value: filters.batch_number });
        }

        // 供应商：使用 supplierMap 展示名称
        if (filters.supplier_id) {
            const ids = filters.supplier_id.split(",");
            const allNames = ids.map((id) => supplierMap[id] || id);
            items.push({
                label: "供应商",
                value: formatArraySummary(ids, supplierMap, 2, "家"),
                fullValue: allNames.join(" / "),
            });
        }

        // 费用类型
        if (filters.cost_type) {
            const types = filters.cost_type.split(",");
            const resolver = (v) => STANDARD_COST_TYPE_LABELS[v.toUpperCase()] || v;
            const allLabels = types.map(resolver);
            items.push({
                label: "费用类型（预分类）",
                value: formatArraySummary(types, resolver, 2, "种"),
                fullValue: allLabels.join(" / "),
            });
        }

        // 来源类型 —— 始终显示
        if (filters.source_type) {
            const sourceArr = filters.source_type.split(",");
            const allLabels = sourceArr.map((s) => SOURCE_TYPE_LABELS[s] || s);
            const fullValue = allLabels.join(" / ");
            const isDefault = sourceArr.length === DEFAULT_SOURCE_TYPE_SET.size && sourceArr.every((s) => DEFAULT_SOURCE_TYPE_SET.has(s));
            const value = formatArraySummary(sourceArr, SOURCE_TYPE_LABELS);
            items.push({
                label: "来源",
                value: isDefault ? `${value} (默认)` : value,
                fullValue,
            });
        }

        // 机场
        if (filters.airport_code) {
            items.push({ label: "机场", value: filters.airport_code });
        }

        // 搜索关键词
        if (filters.search) {
            items.push({ label: "搜索", value: filters.search.length > 10 ? filters.search.substring(0, 10) + "..." : filters.search });
        }

        // 账单内容搜索
        if (filters.structured_data_search) {
            const val = filters.structured_data_search;
            items.push({ label: "账单内容", value: val.length > 10 ? val.substring(0, 10) + "..." : val });
        }

        // 日期
        if (filters.created_at_start || filters.created_at_end) {
            const start = filters.created_at_start || "...";
            const end = filters.created_at_end || "...";
            items.push({ label: "日期", value: `${start} ~ ${end}` });
        }

        // 创建人
        if (filters.created_by_user_id) {
            // 如果是当前用户自己，显示“我的”
            const isMe = user && filters.created_by_user_id === user.id;
            items.push({ label: "创建人", value: isMe ? "我的 (默认)" : `用户 ${filters.created_by_user_id}` });
        }

        return items;
    }, [filters, supplierMap, user]);

    // 业务逻辑 hooks
    const { loading: actionLoading, fetchList, deleteRecord, batchDeleteRecords } = useInvoiceDataSourceActions();

    // 使用 ref 跟踪依赖变化，避免不必要的请求
    const prevFiltersRef = useRef(JSON.stringify(filters));
    const prevPaginationRef = useRef(JSON.stringify({ current: pagination.current, pageSize: pagination.pageSize }));
    const initialLoadRef = useRef(true);

    // 加载数据 - 直接使用 filters（已是 API 格式）
    const fetchData = useCallback(async () => {
        if (loading) return; // 防止重复请求

        setLoading(true);
        try {
            // 构建请求参数：合并分页参数和筛选参数
            const requestParams = {
                page: pagination.current,
                page_size: pagination.pageSize,
                ...filters, // 直接展开筛选参数，已经是API格式
            };

            console.log("🔍 前端请求参数:", requestParams);

            const result = await fetchList(requestParams);

            console.log("📦 后端返回原始数据:", result);
            console.log("📊 数据项数量:", result.items ? result.items.length : 0);
            console.log("📈 总记录数:", result.total);

            setData(result.items || []);
            setPagination((prev) => ({
                ...prev,
                total: result.total || 0,
                current: result.page || pagination.current,
                pageSize: result.page_size || prev.pageSize,
            }));
        } catch (error) {
            console.error("加载数据失败:", error);
            message.error("加载数据失败");
        } finally {
            setLoading(false);
        }
    }, [loading, pagination.current, pagination.pageSize, filters, fetchList]);

    // 响应式数据获取 - 监听 filters 和 pagination 的变化
    useEffect(() => {
        const currentFilters = JSON.stringify(filters);
        const currentPagination = JSON.stringify({ current: pagination.current, pageSize: pagination.pageSize });

        // 只在真正变化或初始加载时获取数据
        if (initialLoadRef.current || currentFilters !== prevFiltersRef.current || currentPagination !== prevPaginationRef.current) {
            fetchData();
            prevFiltersRef.current = currentFilters;
            prevPaginationRef.current = currentPagination;
            if (initialLoadRef.current) {
                initialLoadRef.current = false;
            }
        }
    }, [filters, pagination.current, pagination.pageSize, fetchData]);

    // 搜索 - 接收筛选组件传递的已格式化参数
    const handleSearch = (searchFilters) => {
        setFilters(searchFilters);
        setPagination((prev) => ({ ...prev, current: 1 }));
        // 同步快捷搜索框的值
        setQuickSearchValue(searchFilters.search || "");
        setInvoiceContentSearchValue(searchFilters.structured_data_search || "");
    };

    // 重置搜索 - 恢复默认筛选条件（不包含创建人筛选，看到所有数据）
    const handleReset = useCallback(() => {
        setFilters({ ...DEFAULT_FILTER_VALUES.api });
        setPagination((prev) => ({ ...prev, current: 1 }));
        setQuickSearchValue("");
        setInvoiceContentSearchValue("");
        if (quickSearchDebounceRef.current) clearTimeout(quickSearchDebounceRef.current);
        if (invoiceContentDebounceRef.current) clearTimeout(invoiceContentDebounceRef.current);
    }, []);

    // 快捷搜索框 change 处理（带防抖）
    const handleQuickSearchChange = useCallback(
        (e) => {
            const value = e.target.value;
            setQuickSearchValue(value);
            if (quickSearchDebounceRef.current) clearTimeout(quickSearchDebounceRef.current);
            quickSearchDebounceRef.current = setTimeout(() => {
                setFilters((prev) => {
                    const next = { ...prev };
                    if (value.trim()) {
                        next.search = value.trim();
                    } else {
                        delete next.search;
                    }
                    return next;
                });
                setPagination((prev) => ({ ...prev, current: 1 }));
            }, 500);
        },
        [],
    );

    // 全选当前页面所有记录
    const handleSelectAllVisible = useCallback(() => {
        const selectableRows = data.filter((item) => !idsBatchSelectionDisabled(item));
        const visibleKeys = selectableRows.map((item) => item.id);
        handleRowSelectionChange(visibleKeys, selectableRows);
    }, [data, handleRowSelectionChange]);

    // 表格变化 - 只更新pagination state，由useEffect自动触发数据获取
    const handleTableChange = (newPagination, tableFilters, sorter) => {
        setPagination(newPagination);
    };

    // 查看详情
    const handleViewDetail = (record) => {
        setCurrentRecord(record);
        setDetailDrawerVisible(true);
    };

    // 根据ID查看详情
    const handleViewDetailById = async (id) => {
        try {
            // 在当前数据中查找
            const record = data.find((item) => item.id === id);
            if (record) {
                handleViewDetail(record);
            } else {
                // 如果不在当前页，需要调用API查询
                const response = await settlementService.getInvoiceDataSource(id);
                if (response.data) {
                    handleViewDetail(response.data);
                } else {
                    message.warning("找不到该文件记录");
                }
            }
        } catch (error) {
            console.error("查询文件详情失败:", error);
            message.error("查询文件详情失败");
        }
    };

    // 处理详情页的更新回调
    const handleDetailUpdate = (updateInfo) => {
        // 如果是查看其他文件的操作
        if (updateInfo && typeof updateInfo === "object" && updateInfo.action === "view") {
            handleViewDetailById(updateInfo.id);
        } else {
            // 否则就是普通的刷新列表
            fetchData();
        }
    };

    // 删除记录
    const handleDelete = async (id) => {
        try {
            await deleteRecord(id);
            // ✅ 提示消息已在 deleteRecord 中处理，此处不再重复显示
            fetchData();
        } catch (error) {
            // ❌ 错误提示也已在 deleteRecord 中处理
            fetchData();
        }
    };

    // 对账审核 — 跳转到工作台（该 IDS 所在批次）
    const handleAuditSettlement = (record) => {
        if (!record.batch_number || !record.vendor_id) {
            message.warning("该账单缺少批次信息，无法打开工作台");
            return;
        }
        const params = new URLSearchParams({
            batch: record.batch_number,
            vendor: record.vendor_id,
            costType: record.standard_cost_type || "",
        });
        window.open(`/settlement/ap-workbench?${params.toString()}`, "_blank");
    };

    // 批量删除
    const handleBatchDelete = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning("请选择要删除的记录");
            return;
        }

        try {
            await batchDeleteRecords(selectedRowKeys);
            // ✅ 提示消息已在 batchDeleteRecords 中处理，此处不再重复显示
            clearSelection(); // 使用 hook 提供的清除方法，而不是直接设置状态
            fetchData();
        } catch (error) {
            // ❌ 错误提示也已在 batchDeleteRecords 中处理
            // 但保留 catch 以确保出错后也能清理状态
            clearSelection();
            fetchData();
        }
    };

    // 上传成功回调
    const handleUploadSuccess = () => {
        fetchData();
        setUploadDrawerVisible(false);
    };

    // 批量修改批次号 — 前端预校验后打开弹窗
    const handleOpenBatchUpdateModal = useCallback(() => {
        if (selectedRows.length === 0) {
            message.warning("请先选择要修改的记录");
            return;
        }

        // 前端预校验：选中记录必须属于同一供应商 + 同一费用类型
        const vendorIds = new Set();
        const costTypes = new Set();
        for (const row of selectedRows) {
            if (row.vendor_id != null) vendorIds.add(row.vendor_id);
            if (row.standard_cost_type) costTypes.add(row.standard_cost_type.toUpperCase());
        }

        if (vendorIds.size > 1) {
            message.warning("选中的记录包含不同的供应商，批量修改批次号要求所有记录属于同一供应商");
            return;
        }
        if (costTypes.size > 1) {
            message.warning("选中的记录包含不同的费用类型，批量修改批次号要求所有记录属于同一费用类型");
            return;
        }

        setBatchUpdateModalVisible(true);
    }, [selectedRows]);

    // 表格列定义
    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "文件名",
            dataIndex: "original_filename",
            key: "original_filename",
            width: 180,
            render: (text, record) => {
                // ========== V4.2 新增：文件组记录显示 ==========
                if (record.record_type === "FILE_GROUP") {
                    const fileCount = record.group_files?.length || 0;
                    return (
                        <Space size={4}>
                            {/* 文件夹图标 */}
                            <FolderOutlined style={{ color: "#fa8c16", fontSize: 16 }} />
                            <Tooltip title={`文件组: ${text}`}>
                                <span
                                    style={{
                                        display: "inline-block",
                                        maxWidth: "150px",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        fontWeight: 500,
                                    }}
                                >
                                    {text || "未命名组"}
                                </span>
                            </Tooltip>
                            {/* 文件数量标签 */}
                            <Tag color="orange" style={{ margin: 0 }}>
                                {fileCount}个文件
                            </Tag>
                        </Space>
                    );
                }

                // ========== 单文件或旧格式记录显示 ==========
                // 根据来源类型显示友好的文件名
                const getDisplayName = () => {
                    if (text) return text;

                    // 对于没有文件名的记录，根据来源类型显示友好文本
                    // 优先使用来源类型标签（来自统一常量）
                    if (SOURCE_TYPE_LABELS[record.source_type]) {
                        return SOURCE_TYPE_LABELS[record.source_type];
                    }

                    // 对于UPLOAD类型无文件名的，如果id有意义就显示id（如：CT107-MIGRATION-20250221）
                    if (record.source_type === "UPLOAD" && record.id) {
                        // 如果id看起来像UUID格式（全小写字母和数字加连字符），显示"未知文件"
                        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(record.id);
                        if (!isUUID) {
                            return record.id; // 显示有意义的ID（如迁移批次号）
                        }
                    }

                    return "未知文件";
                };

                return (
                    <Space size={4}>
                        <Tooltip title={text || getDisplayName()}>
                            <span
                                style={{
                                    display: "inline-block",
                                    maxWidth: "240px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {getDisplayName()}
                            </span>
                        </Tooltip>
                        {/* 关联组标识（旧格式兼容） */}
                        {record.file_group_id && !record.record_type && (
                            <Tooltip title={`属于文件组: ${record.file_group_name || "未命名组"}`}>
                                <LinkOutlined style={{ color: "#1890ff", fontSize: 14 }} />
                            </Tooltip>
                        )}
                        {/* 拆分文件标识 */}
                        {record.is_split && (
                            <Tooltip title={`拆分文件 (来自: ${record.split_from_filename || "原始文件"}, 页面: ${record.split_pages || "未知"})`}>
                                <ScissorOutlined style={{ color: "#52c41a", fontSize: 14 }} />
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: "供应商",
            dataIndex: "vendor_name",
            key: "vendor_name",
            width: 180,
            render: (vendorName, record) => {
                if (!vendorName) return "-";
                return record.vendor_code ? `${vendorName} (${record.vendor_code})` : vendorName;
            },
        },
        {
            title: "费用类型（预分类）",
            dataIndex: "standard_cost_type",
            key: "standard_cost_type",
            width: 100,
            render: (costType) => {
                if (!costType) return "-";
                // 转换为大写以匹配常量定义
                const upperCostType = costType.toUpperCase();
                const label = STANDARD_COST_TYPE_LABELS[upperCostType] || costType;
                const color = STANDARD_COST_TYPE_COLORS[upperCostType] || "default";
                return <Tag color={color}>{label}</Tag>;
            },
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 160,
            render: (status, record) => {
                // PARTNER_BILLING 类型：显示门户发布状态（替代无意义的 IDS 处理状态）
                if (record.source_type === "PARTNER_BILLING") {
                    const pubs = record.portal_publications;
                    if (!pubs || pubs.length === 0) return <Tag>未指定合作方</Tag>;
                    const PORTAL_STATUS_MAP = {
                        DRAFT: { color: "default", text: "待发布" },
                        PUBLISHED: { color: "processing", text: "已发布" },
                        REVOKED: { color: "warning", text: "已撤回" },
                    };
                    // 取第一条记录展示（同一 IDS 通常只关联一个合作方）
                    const pub = pubs[0];
                    const cfg = PORTAL_STATUS_MAP[pub.portal_status] || { color: "default", text: pub.portal_status };
                    return (
                        <Tooltip title={`合作方: ${pub.customer_name || "未知"}`}>
                            <Tag color={cfg.color}>{cfg.text}</Tag>
                        </Tooltip>
                    );
                }
                // 其他类型：显示 IDS 处理状态
                const label = INVOICE_DATA_SOURCE_STATUS_LABELS[status] || status;
                const color = INVOICE_DATA_SOURCE_STATUS_COLORS[status] || "default";
                const voidReason = status === "VOIDED" ? getVoidReason(record) : null;
                const replacementId = status === "VOIDED" ? getReplacementId(record) : null;
                const replacementLabel = status === "VOIDED" ? getReplacementLabel(record) : null;
                const replacesId = getReplacesId(record);
                const replacesLabel = getReplacesLabel(record);
                return (
                    <Space direction="vertical" size={2}>
                        <Tag color={color}>{label}</Tag>
                        {voidReason && (
                            <Tooltip title={voidReason}>
                                <Text type="secondary" style={{ fontSize: 12, maxWidth: 120 }} ellipsis>
                                    原因：{voidReason}
                                </Text>
                            </Tooltip>
                        )}
                        {replacementId && (
                            <Button type="link" size="small" onClick={(e) => { e.stopPropagation(); handleViewDetailById(replacementId); }} style={{ padding: 0, height: "auto", fontSize: 12 }}>
                                已被 {replacementLabel || replacementId} 替代
                            </Button>
                        )}
                        {replacesId && (
                            <Button type="link" size="small" onClick={(e) => { e.stopPropagation(); handleViewDetailById(replacesId); }} style={{ padding: 0, height: "auto", fontSize: 12 }}>
                                替代了 {replacesLabel || replacesId}
                            </Button>
                        )}
                    </Space>
                );
            },
        },
        {
            title: "批次号",
            dataIndex: "batch_number",
            key: "batch_number",
            width: 120,
            render: (batchNumber) => {
                if (!batchNumber) return "-";
                return <Tooltip title={batchNumber}>{batchNumber}</Tooltip>;
            },
        },
        {
            title: "机场",
            dataIndex: "airport_code",
            key: "airport_code",
            width: 40,
            render: (airportCode, record) => {
                if (!airportCode) return "-";
                return (
                    <Tooltip title={record.airport_name || airportCode}>
                        <Tag color="blue">{airportCode}</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: "批次验证",
            dataIndex: "structured_data",
            key: "batch_validation",
            width: 100,
            render: (structuredData, record) => {
                const batchValidations = structuredData?.batch_validations || [];
                if (batchValidations.length === 0) {
                    return <Text type="secondary">-</Text>;
                }

                // 获取最近的一次验证
                const latestValidation = [...batchValidations].sort((a, b) => new Date(b.validated_at) - new Date(a.validated_at))[0];

                // 状态配置
                const statusConfig = {
                    PASSED: { color: "success", icon: <CheckCircleOutlined />, text: "通过" },
                    HAS_ISSUES: { color: "error", icon: <CloseCircleOutlined />, text: "问题" },
                    PENDING: { color: "processing", icon: <ClockCircleOutlined />, text: "验证中" },
                    UNKNOWN: { color: "default", icon: <WarningOutlined />, text: "未知" },
                };
                const config = statusConfig[latestValidation.status] || statusConfig.UNKNOWN;

                return (
                    <Tooltip title={`最近验证: ${new Date(latestValidation.validated_at).toLocaleString()}${batchValidations.length > 1 ? ` (共 ${batchValidations.length} 次)` : ""}`}>
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                // TODO: 批次验证详情弹窗（待实现）
                                console.log('查看批次验证:', latestValidation.action_item_id, record.id);
                            }}
                        >
                            <Tag color={config.color} icon={config.icon} style={{ cursor: "pointer" }}>
                                {config.text}
                                {batchValidations.length > 1 && ` (${batchValidations.length})`}
                            </Tag>
                        </Button>
                    </Tooltip>
                );
            },
        },
        {
            title: "来源类型",
            dataIndex: "source_type",
            key: "source_type",
            width: 60,
            render: (type) => {
                const label = SOURCE_TYPE_LABELS[type] || type;
                const color = SOURCE_TYPE_COLORS[type] || "default";
                return <Tag color={color}>{label}</Tag>;
            },
        },
        // {
        //     title: "解析版本",
        //     dataIndex: "parser_version",
        //     key: "parser_version",
        //     width: 100,
        // },
        // {
        //     title: "是否激活",
        //     dataIndex: "is_active_parser",
        //     key: "is_active_parser",
        //     width: 100,
        //     render: (isActive) => <Tag color={isActive ? "success" : "default"}>{isActive ? "是" : "否"}</Tag>,
        // },
        // "创建时间"列已移除，仍可在详情抽屉中查看
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 120,
            render: (time) => (time ? new Date(time).toLocaleString() : "-"),
        },
        {
            title: "操作",
            key: "action",
            width: 200,
            fixed: "right",
            render: (_, record) => (
                <Space size="small" className="table-actions">
                    <PermissionGuard permission="settlement_ap_invoice_data_source:read">
                        <Button
                            type="link"
                            size="small"
                            // icon={<EyeOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetail(record);
                            }}
                        >
                            详情
                        </Button>
                    </PermissionGuard>{" "}
                    {/* 跳转工作台 — 需要有批次信息才显示 */}
                    {record.batch_number && record.vendor_id && (
                        <PermissionGuard permission="settlement_ap_reconciliation:read">
                            <Button
                                type="link"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleAuditSettlement(record);
                                }}
                            >
                                工作台
                            </Button>
                        </PermissionGuard>
                    )}
                    {hasPerIdsPipelineActions(record.status) && (
                        <InvoiceDataSourceActionMenu record={record} onSuccess={fetchData} triggerType="link" buttonText="重试" />
                    )}
                    <PermissionGuard permission="settlement_ap_invoice_data_source:delete">
                        <Popconfirm
                            title="确认删除"
                            description={getDeleteConfirmDescription(record)}
                            onConfirm={(e) => {
                                e?.stopPropagation();
                                handleDelete(record.id);
                            }}
                            okText="确认"
                            cancelText="取消"
                        >
                            <Button type="link" size="small" danger onClick={(e) => e.stopPropagation()}>
                                删除
                            </Button>
                        </Popconfirm>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact">
                <div className="page-title-compact">应付账单上传</div>
                <Space>
                    <PermissionGuard permission="settlement_ap_invoice_data_source:create">
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setUploadDrawerVisible(true)}>
                            批量上传
                        </Button>
                    </PermissionGuard>
                    <Button icon={<ReloadOutlined />} onClick={() => fetchData()} loading={loading}>
                        刷新
                    </Button>
                </Space>
            </div>

            {/* 搜索过滤 - 紧凑模式 */}
            <CollapsibleFilterBar
                summaryItems={filterSummaryItems}
                onReset={handleReset}
                drawerTitle="应付账单上传 - 筛选条件"
                quickControls={
                    <Space size={8}>
                        <Input
                            placeholder="搜索文件名/供应商/批次号/机场..."
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            size="small"
                            style={{ width: 260, height: 26 }}
                            value={quickSearchValue}
                            onChange={handleQuickSearchChange}
                            allowClear
                            onClear={() => {
                                setQuickSearchValue("");
                                setFilters((prev) => {
                                    const next = { ...prev };
                                    delete next.search;
                                    return next;
                                });
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                        />
                        <Input
                            placeholder="搜索账单内容..."
                            prefix={<FileTextOutlined style={{ color: '#bfbfbf' }} />}
                            size="small"
                            style={{ width: 200, height: 26 }}
                            value={invoiceContentSearchValue}
                            onChange={(e) => {
                                const value = e.target.value;
                                setInvoiceContentSearchValue(value);
                                if (invoiceContentDebounceRef.current) clearTimeout(invoiceContentDebounceRef.current);
                                invoiceContentDebounceRef.current = setTimeout(() => {
                                    setFilters((prev) => {
                                        const next = { ...prev };
                                        if (value.trim()) {
                                            next.structured_data_search = value.trim();
                                        } else {
                                            delete next.structured_data_search;
                                        }
                                        return next;
                                    });
                                    setPagination((prev) => ({ ...prev, current: 1 }));
                                }, 800);
                            }}
                            allowClear
                            onClear={() => {
                                setInvoiceContentSearchValue("");
                                if (invoiceContentDebounceRef.current) clearTimeout(invoiceContentDebounceRef.current);
                                setFilters((prev) => {
                                    const next = { ...prev };
                                    delete next.structured_data_search;
                                    return next;
                                });
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                        />
                    </Space>
                }
            >
                <InvoiceDataSourceFilter onSearch={handleSearch} onReset={handleReset} loading={loading} defaultCreatedByUserId={user?.id} />
            </CollapsibleFilterBar>

            {/* 批量操作工具栏 - fixed 悬浮定位（已内置于 BatchActionsBar 组件） */}
            {selectedRowKeys.length > 0 && (
                    <BatchActionsBar
                        selectedCount={selectedRowKeys.length}
                        itemName="个数据源"
                        onSelectAll={handleSelectAllVisible}
                        onClearSelection={clearSelection}
                        actions={[
                                    {
                                        key: "batch-update-batch-number",
                                        label: "批量修改批次号",
                                        icon: <NumberOutlined />,
                                        render: () => (
                                            <PermissionGuard permission="settlement_ap_invoice_data_source:create">
                                                <Button size="small" type="primary" ghost icon={<NumberOutlined />} onClick={handleOpenBatchUpdateModal} style={{ fontSize: "12px" }}>
                                                    批量修改批次号 ({selectedRowKeys.length})
                                                </Button>
                                            </PermissionGuard>
                                        ),
                                    },
                                    {
                                        key: "delete",
                                        label: "批量删除",
                                        icon: <DeleteOutlined />,
                                        danger: true,
                                        loading: actionLoading,
                                        showCount: true,
                                        render: () => (
                                            <PermissionGuard permission="settlement_ap_invoice_data_source:delete">
                                                <Popconfirm
                                                    title="批量删除"
                                                    description={(
                                                        <div>
                                                            <div>确认删除选中的 {selectedRowKeys.length} 条记录吗？后端会逐条校验并返回结果。</div>
                                                            {selectedRows.some((row) => getIdsDeleteMode(row) === "running") && <div>运行中的记录会先停止关联执行，停止失败则不会删除。</div>}
                                                            {selectedRows.some((row) => getIdsDeleteMode(row) === "protected") && <div>已确认入库或已作废的记录会被后端阻断删除。</div>}
                                                        </div>
                                                    )}
                                                    onConfirm={handleBatchDelete}
                                                    okText="确认"
                                                    cancelText="取消"
                                                >
                                                    <Button size="small" type="primary" ghost danger icon={<DeleteOutlined />} loading={actionLoading} style={{ fontSize: "12px" }}>
                                                        批量删除 ({selectedRowKeys.length})
                                                    </Button>
                                                </Popconfirm>
                                            </PermissionGuard>
                                        ),
                                    },
                                    {
                                        key: "assign-partner",
                                        label: "指定合作方",
                                        icon: <TeamOutlined />,
                                        render: () => (
                                            <PermissionGuard permission="settlement_ap_invoice_data_source:create">
                                                <Button size="small" type="primary" ghost icon={<TeamOutlined />} onClick={() => setAssignPartnerModalVisible(true)} style={{ fontSize: "12px" }}>
                                                    指定合作方 ({selectedRowKeys.length})
                                                </Button>
                                            </PermissionGuard>
                                        ),
                                    },
                                    {
                                        key: "portal-publish",
                                        label: "发布到门户",
                                        icon: <SendOutlined />,
                                        render: () => (
                                            <PermissionGuard permission="settlement_ap_invoice_data_source:update">
                                                <Popconfirm
                                                    title="发布到门户"
                                                    description={`确认将选中的 ${selectedRowKeys.length} 条记录发布到客户门户吗？发布后合作方即可查看。`}
                                                    onConfirm={async () => {
                                                        try {
                                                            // 直接从选中记录的 portal_publications 字段提取 publication IDs
                                                            const pubIds = selectedRows
                                                                .flatMap(r => (r.portal_publications || []).map(p => p.id))
                                                                .filter(Boolean);
                                                            if (pubIds.length === 0) {
                                                                message.warning("选中的记录没有关联的门户发布记录，请先「指定合作方」");
                                                                return;
                                                            }
                                                            await settlementService.portalPublication.batchPublish({ publication_ids: pubIds });
                                                            message.success(`成功发布 ${pubIds.length} 条记录`);
                                                            clearSelection();
                                                            fetchData();
                                                        } catch (error) {
                                                            message.error("发布失败: " + (error.response?.data?.detail || error.message));
                                                        }
                                                    }}
                                                    okText="确认发布"
                                                    cancelText="取消"
                                                >
                                                    <Button size="small" type="primary" ghost icon={<SendOutlined />} style={{ fontSize: "12px" }}>
                                                        发布到门户 ({selectedRowKeys.length})
                                                    </Button>
                                                </Popconfirm>
                                            </PermissionGuard>
                                        ),
                                    },
                                    {
                                        key: "check-sync",
                                        label: (
                                            <PermissionGuard permission="settlement_ap_invoice_data_source:read" fallback={null}>
                                                <Button
                                                    size="small"
                                                    icon={<SyncOutlined />}
                                                    style={{ fontSize: "12px" }}
                                                    onClick={async () => {
                                                        // 找到选中记录中所有 PARTNER_BILLING 类型的 publication IDs
                                                        const partnerRecords = selectedRows.filter(r => r.source_type === "PARTNER_BILLING");
                                                        if (partnerRecords.length === 0) {
                                                            message.warning("请选择合作方账单类型的记录");
                                                            return;
                                                        }
                                                        const allPubIds = partnerRecords
                                                            .flatMap(r => (r.portal_publications || []).map(p => p.id))
                                                            .filter(Boolean);
                                                        if (allPubIds.length === 0) {
                                                            message.warning("选中的记录没有关联的门户发布记录");
                                                            return;
                                                        }
                                                        try {
                                                            const res = await settlementService.portalPublication.checkSyncStatus(allPubIds);
                                                            const syncStatus = res.data?.data?.sync_status || {};
                                                            const syncedCount = Object.values(syncStatus).filter(v => v.synced).length;
                                                            const totalCount = Object.keys(syncStatus).length;
                                                            Modal.info({
                                                                title: "Portal 同步状态",
                                                                content: (
                                                                    <div>
                                                                        <p>已同步: <Tag color="success">{syncedCount}</Tag> / 共 {totalCount} 条</p>
                                                                        {Object.entries(syncStatus).map(([pubId, info]) => (
                                                                            <div key={pubId} style={{ marginBottom: 4 }}>
                                                                                发布记录 #{pubId}：
                                                                                {info.synced
                                                                                    ? <Tag color="success">已同步 ({info.synced_at})</Tag>
                                                                                    : <Tag color="warning">未同步</Tag>
                                                                                }
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ),
                                                            });
                                                        } catch (error) {
                                                            message.error("查询同步状态失败: " + (error.response?.data?.detail || error.message));
                                                        }
                                                    }}
                                                >
                                                    查询同步状态 ({selectedRowKeys.length})
                                                </Button>
                                            </PermissionGuard>
                                        ),
                                    },
                                ]}
                            />
            )}

            {/* 数据列表 */}
            <Card className="data-card">
                {/* 数据表格 */}
                <Table
                    size="small"
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    pagination={pagination}
                    loading={loading}
                    rowSelection={
                        hasPermission("settlement_ap_invoice_data_source:delete")
                            ? {
                                  ...rowSelection,
                                  columnWidth: 6, // 明确设置选择框列宽
                                  getCheckboxProps: (record) => ({
                                      ...rowSelection.getCheckboxProps?.(record),
                                      disabled: idsBatchSelectionDisabled(record),
                                  }),
                              }
                            : null
                    }
                    onChange={handleTableChange}
                    scroll={{ x: 1200 }}
                    ref={selectionTableRef}
                    onRow={getRowProps}
                />
            </Card>

            {/* 上传抽屉 */}
            <UploadDrawer open={uploadDrawerVisible} onClose={() => setUploadDrawerVisible(false)} onSuccess={handleUploadSuccess} />

            {/* 详情抽屉 */}
            <InvoiceDataSourceDetailDrawer open={detailDrawerVisible} onClose={() => setDetailDrawerVisible(false)} record={currentRecord} onUpdate={fetchData} />

            {/* 批量修改批次号弹窗 */}
            <BatchUpdateBatchNumberModal
                open={batchUpdateModalVisible}
                onClose={() => setBatchUpdateModalVisible(false)}
                selectedIds={selectedRowKeys}
                onSuccess={() => {
                    clearSelection();
                    fetchData();
                }}
            />

            {/* 批量指定合作方弹窗 */}
            <BatchAssignPartnerModal
                open={assignPartnerModalVisible}
                onClose={() => setAssignPartnerModalVisible(false)}
                selectedIds={selectedRowKeys}
                onSuccess={() => {
                    clearSelection();
                    fetchData();
                }}
            />



        </div>
    );
};

export default InvoiceDataSourcePage;
